import { Box, Stack, type Theme } from "@mui/material";
import { APP_V2_USER_EVENTS } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { type ResolvedCancellationProtectionPayout } from "@src/appV2/redesign/CancellationProtectionPayout/api/types";
import { NEARBY_PAYOUT_LOOKBACK_DAYS } from "@src/appV2/redesign/CancellationProtectionPayout/api/useNearbyCancellationProtectionPayouts";
import { LoadingSpinner, Text } from "@src/appV2/redesign/system/ui-components";
import pluralize from "pluralize";
import { Virtuoso } from "react-virtuoso";

import { OthersPayoutCard } from "./OthersPayoutCard";

interface OthersReceivingProtectionListProps {
  payouts: ResolvedCancellationProtectionPayout[];
  totalCountLabel: string;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
}

export function OthersReceivingProtectionList(props: OthersReceivingProtectionListProps) {
  const { payouts, totalCountLabel, hasNextPage, isFetchingNextPage, fetchNextPage } = props;

  useLogEffect(APP_V2_USER_EVENTS.CANCELLATION_PROTECTION_OTHERS_TAB_VIEWED, {
    payoutsCount: payouts.length,
    hasNextPage,
  });

  return (
    <Virtuoso
      style={{ height: "100%" }}
      data={payouts}
      endReached={() => {
        if (hasNextPage && !isFetchingNextPage) {
          void fetchNextPage();
        }
      }}
      components={{
        Header: () => (
          <Stack alignItems="center" sx={{ paddingTop: 9, paddingBottom: 4, paddingX: 5 }}>
            <Text
              variant="body2"
              sx={(theme: Theme) => ({
                color: theme.palette.text.tertiary,
                textAlign: "center",
              })}
            >
              {totalCountLabel} {pluralize("worker", payouts.length)} near you{" "}
              {pluralize("was", payouts.length)} protected in the last {NEARBY_PAYOUT_LOOKBACK_DAYS}{" "}
              {pluralize("day", NEARBY_PAYOUT_LOOKBACK_DAYS)}
            </Text>
          </Stack>
        ),
        Footer: () => {
          if (isFetchingNextPage) {
            return (
              <Stack alignItems="center" sx={{ paddingY: 5 }}>
                <LoadingSpinner size="medium" />
              </Stack>
            );
          }

          return <Box sx={{ paddingBottom: 11 }} />;
        },
      }}
      itemContent={(_, payout) => (
        <Box sx={{ paddingBottom: 4, paddingX: 5 }}>
          <OthersPayoutCard payout={payout} />
        </Box>
      )}
    />
  );
}
